import { useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import "./ContactAudit.css";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { IconMail, IconCalendar, IconHome, IconX } from "@tabler/icons-react";
import { SOCIALS } from "../../config";
import { openBooking } from "../../utils/booking";
import { useTransitionNavigate } from "../../hooks/useTransitionNavigate";

export default function ContactAudit() {
  const transitionTo = useTransitionNavigate();
  const goHome = (e) => {
    e.preventDefault();
    transitionTo("/");
  };
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const optionsRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isHidden = [
    "/rouhi",
    "/rouhi3",
    "/rouhi4",
    "/amalna5",
    "/amalna6",
    "/ourstory6",
    "/rouhi6",
  ].includes(location.pathname);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  // Open: the cream "Let's talk" button splits sideways into two buttons
  // (E-mail | Book a call) - the wrapper widens to fit them and drops its own
  // fill (.is-open) so the gap between them shows the dock.
  useGSAP(
    () => {
      const spring = { duration: 0.8, ease: "elastic.out(1, 0.75)" };

      if (isOpen) {
        gsap.to(wrapperRef.current, { width: optionsRef.current.offsetWidth, ...spring });
        gsap.to(".contact-audit-label", { autoAlpha: 0, duration: 0.15 });
        gsap.to(".contact-audit-options", { autoAlpha: 1, duration: 0.2, delay: 0.05 });
      } else {
        gsap.to(".contact-audit-options", { autoAlpha: 0, duration: 0.15 });
        gsap.to(wrapperRef.current, { width: 120, ...spring });
        gsap.to(".contact-audit-label", { autoAlpha: 1, duration: 0.3, delay: 0.1 });
      }
    },
    { scope: containerRef, dependencies: [isOpen] },
  );

  const handleMouseEnter = () => setIsOpen(true);
  const handleMouseLeave = () => setIsOpen(false);
  const handleClick = () => setIsOpen(true);
  // Touch has no mouseleave, so the open dock gets an X to fold back up.
  // stopPropagation keeps the wrapper's onClick from reopening it.
  const handleClose = (e) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  if (isHidden) return null;

  return (
    <div
      className={`contact-audit-container ${!isHome ? "has-home-btn" : ""}`}
      ref={containerRef}
    >
      {/* Page-specific dock buttons portal in here (e.g. PaletteSelector) */}
      <div className="dock-slot" />
      <div
        className={`contact-audit-wrapper ${isOpen ? "is-open" : ""}`}
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <div className="contact-audit-label">Let's talk</div>

        <div className="contact-audit-options" ref={optionsRef}>
          <a href={`mailto:${SOCIALS.email}`} className="btn contact-audit-option">
            <IconMail size={20} stroke={1.5} />
            <span>E-mail</span>
          </a>
          <button
            type="button"
            className="btn contact-audit-option"
            onClick={(e) => {
              handleClose(e);
              openBooking();
            }}
          >
            <IconCalendar size={20} stroke={1.5} />
            <span>Book a call</span>
          </button>
          <button
            type="button"
            className="btn btn--icon contact-audit-close"
            aria-label="Close contact options"
            onClick={handleClose}
          >
            <IconX size={20} stroke={1.5} />
          </button>
        </div>
      </div>
      {!isHome && (
        <Link to="/" className="btn btn--icon" aria-label="Go to Home" onClick={goHome}>
          <IconHome size={20} stroke={1.5} />
        </Link>
      )}
    </div>
  );
}
